import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { t, translateError } from '../i18n.js';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import SearchBar from '../components/shared/SearchBar.jsx';
import Filters, { MultiFilterSelect } from '../components/shared/Filters.jsx';
import { parseServerDate } from '../utils/dates.js';
import { DepartmentLabelById } from '../components/shared/DepartmentLabel.jsx';

const STATUSES = ['nieuw', 'actief', 'wacht_op_input', 'afgerond', 'geannuleerd'];

function ProcessesSkeleton() {
  return (
    <div class="card mt-4" style="overflow:hidden">
      <div style="display:grid;grid-template-columns:1fr 160px 120px 100px;gap:12px;padding:10px 16px;background:var(--color-surface-alt,#f9fafb);border-bottom:1px solid var(--color-border)">
        {['40%','20%','15%','10%'].map((w, i) => (
          <div key={i} class="skeleton" style={`height:12px;width:${w};border-radius:4px`} />
        ))}
      </div>
      {[0,1,2,3,4].map(i => (
        <div key={i} style="display:grid;grid-template-columns:1fr 160px 120px 100px;gap:12px;padding:13px 16px;border-bottom:1px solid var(--color-border);align-items:center">
          <div>
            <div class="skeleton" style="height:14px;width:55%;border-radius:4px;margin-bottom:5px" />
            <div class="skeleton" style="height:11px;width:30%;border-radius:4px" />
          </div>
          <div class="skeleton" style="height:13px;width:70%;border-radius:4px" />
          <div class="skeleton" style="height:13px;width:80px;border-radius:4px" />
          <div class="skeleton" style="height:22px;width:80px;border-radius:12px" />
        </div>
      ))}
    </div>
  );
}

export default function ProcessListView() {
  const { hasPermission } = useAuth();

  const [processes, setProcesses] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [statusFilter, setStatusFilter] = useState([]);
  const [search, setSearch]       = useState('');

  const [seasons, setSeasons]         = useState([]);
  const [seasonFilter, setSeasonFilter] = useState('');
  const [modalSeason, setModalSeason]   = useState('');

  const [modalOpen, setModalOpen]           = useState(false);
  const [templates, setTemplates]           = useState([]);
  const [users, setUsers]                   = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedVariant, setSelectedVariant]   = useState('');
  const [processTitle, setProcessTitle]     = useState('');
  const [assignedTo, setAssignedTo]         = useState('');
  const [deadline, setDeadline]             = useState('');
  const [submitting, setSubmitting]         = useState(false);
  const [submitError, setSubmitError]       = useState(null);
  // The department the process runs in (two or more in the club): 'main' = the main club
  const [departmentChoice, setDepartmentChoice] = useState(null);
  const [department, setDepartment]         = useState('');

  const fetchProcesses = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter.length > 0) params.set('status', statusFilter.join(','));
    if (search) params.set('search', search);
    if (seasonFilter) params.set('season_id', seasonFilter);
    const q = params.toString();
    api(`/api/processes${q ? `?${q}` : ''}`)
      .then(setProcesses)
      .catch(err => setError(translateError(err.code, err.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProcesses(); }, [statusFilter, search, seasonFilter]);

  useEffect(() => {
    api('/api/seasons').then(data => {
      setSeasons(Array.isArray(data) ? data : []);
      const active = data.find(s => s.is_active);
      if (active) setModalSeason(String(active.id));
    }).catch(() => {});
  }, []);

  const openModal = () => {
    setSubmitError(null);
    setSelectedTemplate(null);
    setSelectedVariant('');
    setProcessTitle('');
    setAssignedTo('');
    setDeadline('');
    setDepartmentChoice(null);
    Promise.all([
      api('/api/templates').catch(() => []),
      api('/api/users').catch(() => []),
      api('/api/processes/start-options').catch(() => null),
    ]).then(([tpls, usrs, choice]) => {
      setTemplates(Array.isArray(tpls) ? tpls.filter(tpl => tpl.is_active) : []);
      setUsers(Array.isArray(usrs) ? usrs : []);
      setDepartmentChoice(choice?.show ? choice : null);
      setDepartment(choice?.defaultId != null ? String(choice.defaultId) : 'main');
    });
    setModalOpen(true);
  };

  const handleTemplateChange = (templateId) => {
    const tpl = templates.find(t => String(t.id) === String(templateId));
    setSelectedTemplate(tpl || null);
    setSelectedVariant('');
  };

  const handleStart = async () => {
    if (!selectedTemplate) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api('/api/processes/start', {
        method: 'POST',
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          variant_id: selectedVariant || undefined,
          title: processTitle || undefined,
          assigned_to: assignedTo || undefined,
          deadline: deadline || undefined,
          season_id: modalSeason || undefined,
          ...(departmentChoice ? { departmentId: department === 'main' ? null : Number(department) } : {}),
        }),
      });
      setModalOpen(false);
      fetchProcesses();
    } catch (err) {
      setSubmitError(translateError(err.code, err.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (val) => val ? parseServerDate(val).toLocaleDateString('nl-NL') : null;

  const statusOptions = STATUSES.map(s => ({ value: s, label: t(`process.statuses.${s}`) }));

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">{t('process.title')}</h1>
        {hasPermission('processes', 'create') && (
          <Button variant="primary" onClick={openModal}>
            {t('process.newProcess')}
          </Button>
        )}
      </div>

      <Filters>
        <SearchBar value={search} onSearch={setSearch} placeholder={t('common.search')} />
        <MultiFilterSelect
          label={t('common.status')}
          values={statusFilter}
          options={statusOptions}
          onChange={setStatusFilter}
        />
        {seasons.length > 0 && (
          <select
            class="form-input"
            value={seasonFilter}
            onChange={e => setSeasonFilter(e.target.value)}
            style="height:36px;font-size:13px;padding:0 10px;min-width:160px"
          >
            <option value="">Alle seizoenen</option>
            {seasons.map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.is_active ? ' ✓' : ''}</option>
            ))}
          </select>
        )}
      </Filters>

      {loading && <ProcessesSkeleton />}

      {!loading && error && <div class="alert alert-error mt-4">{error}</div>}

      {!loading && !error && processes.length === 0 && (
        <div class="text-center text-muted" style="padding:60px">
          {t('common.noResults')}
        </div>
      )}

      {!loading && !error && processes.length > 0 && (
        <div class="card mt-4" style="overflow:hidden">
          {/* Header row */}
          <div style="display:grid;grid-template-columns:1fr 160px 120px 100px;gap:12px;padding:10px 16px;background:var(--color-surface-alt,#f9fafb);border-bottom:1px solid var(--color-border);font-size:12px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em">
            <span>{t('process.processTitle')}</span>
            <span>{t('process.startedBy')}</span>
            <span>{t('process.deadline')}</span>
            <span>{t('common.status')}</span>
          </div>

          {processes.map(proc => (
            <div
              key={proc.id}
              class="list-item-row"
              style="display:grid;grid-template-columns:1fr 160px 120px 100px;gap:12px;padding:12px 16px;border-bottom:1px solid var(--color-border);align-items:center;cursor:pointer;transition:background .15s"
              onClick={() => route(`/processes/${proc.id}`)}
            >
              <div style="min-width:0">
                <div style="font-size:14px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
                  {proc.title}
                </div>
                <DepartmentLabelById id={proc.department_id} wrap="margin:2px 0" />
                {proc.template_name && (
                  <div class="text-muted" style="font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
                    {proc.template_name}
                  </div>
                )}
              </div>

              <div style="font-size:13px;color:var(--color-text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
                {proc.started_by_name || '—'}
              </div>

              <div style="font-size:13px;color:var(--color-text-muted)">
                {formatDate(proc.deadline) || '—'}
              </div>

              <StatusBadge status={proc.status} />
            </div>
          ))}
        </div>
      )}

      {/* Start process modal */}
      <Modal isOpen={modalOpen} title={t('process.startProcess')} onClose={() => setModalOpen(false)}>
        <div class="form-group">
          <label class="form-label">{t('process.selectTemplate')}</label>
          <select
            class="form-select"
            value={selectedTemplate?.id || ''}
            onChange={e => handleTemplateChange(e.target.value)}
          >
            <option value="">{t('process.selectTemplate')}</option>
            {templates.map(tpl => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
        </div>

        {selectedTemplate?.variants?.length > 0 && (
          <div class="form-group">
            <label class="form-label">{t('process.selectVariant')}</label>
            <select
              class="form-select"
              value={selectedVariant}
              onChange={e => setSelectedVariant(e.target.value)}
            >
              <option value="">{t('process.selectVariant')}</option>
              {selectedTemplate.variants.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
        )}

        {departmentChoice && (
          <div class="form-group">
            <label class="form-label">Afdeling</label>
            {departmentChoice.mainClub || departmentChoice.options.length ? (
              <select class="form-select" value={department} onChange={e => setDepartment(e.target.value)}>
                {departmentChoice.mainClub && <option value="main">Hoofdvereniging (geen afdeling)</option>}
                {departmentChoice.options.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            ) : (
              <div class="alert alert-error">Je rol geldt voor geen enkele afdeling meer. Vraag de clubbeheerder om je rol aan te passen.</div>
            )}
            <p class="text-muted" style="font-size:12px;margin-top:4px">
              Stappen gaan naar wie de rol voor deze afdeling heeft, anders naar wie hem voor de hele club heeft.
            </p>
          </div>
        )}

        <div class="form-group">
          <label class="form-label">{t('process.processTitle')}</label>
          <input
            class="form-input"
            type="text"
            value={processTitle}
            onInput={e => setProcessTitle(e.target.value)}
            placeholder={t('process.processTitle')}
          />
        </div>

        <div class="form-group">
          <label class="form-label">{t('process.assignedTo')}</label>
          <select
            class="form-select"
            value={assignedTo}
            onChange={e => setAssignedTo(e.target.value)}
          >
            <option value="">{t('process.assignedTo')}</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.display_name || u.email}</option>
            ))}
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">
            {t('process.deadline')} <span class="text-muted" style="font-weight:400">({t('common.optional')})</span>
          </label>
          <input
            class="form-input"
            type="date"
            value={deadline}
            onInput={e => setDeadline(e.target.value)}
          />
        </div>

        {seasons.length > 0 && (
          <div class="form-group">
            <label class="form-label">Seizoen</label>
            <select
              class="form-select"
              value={modalSeason}
              onChange={e => setModalSeason(e.target.value)}
            >
              <option value="">Geen seizoen</option>
              {seasons.map(s => (
                <option key={s.id} value={s.id}>{s.name}{s.is_active ? ' ✓' : ''}</option>
              ))}
            </select>
          </div>
        )}

        {submitError && <div class="alert alert-error mb-3">{submitError}</div>}

        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleStart}
            disabled={!selectedTemplate || submitting || (departmentChoice && !departmentChoice.mainClub && !departmentChoice.options.length)}
          >
            {submitting ? t('common.loading') : t('process.startProcess')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
